import { useState, useEffect, useRef } from 'react';

export default function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const hasTriggered = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('exitPopupDismissed');
    if (dismissed) return;

    const timer = setTimeout(() => {
      if (!hasTriggered.current) {
        hasTriggered.current = true;
        setIsVisible(true);
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Start countdown when popup becomes visible
  useEffect(() => {
    if (isVisible && !submitted) {
      setTimeLeft(60);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            handleDismiss();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isVisible, submitted]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleDismiss = () => {
    setIsVisible(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handlePermanentDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('exitPopupDismissed', 'true');
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name.trim());
      body.append('email', email.trim());

      const res = await fetch('https://readdy.ai/api/form/d6kppoc6fojmejgaioc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
        if (timerRef.current) clearInterval(timerRef.current);
        sessionStorage.setItem('exitPopupDismissed', 'true');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isVisible) return null;

  const isUrgent = timeLeft <= 15;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: '#0a0a0a', border: `1px solid ${isUrgent ? '#ff4444' : '#B8860B'}` }}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full transition-all duration-500"
          style={{ background: isUrgent ? 'linear-gradient(90deg, #ff4444, #ff8888, #ff4444)' : 'linear-gradient(90deg, #B8860B, #F5D76E, #B8860B)' }}
        />

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:bg-white/10"
          style={{ color: '#888' }}
          aria-label="Close"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="px-8 py-8">
          {!submitted ? (
            <>
              {/* Badge */}
              <div className="flex justify-center mb-4">
                <span
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase"
                  style={{ background: 'rgba(184,134,11,0.15)', color: '#F5D76E', border: '1px solid rgba(184,134,11,0.4)' }}
                >
                  <i className="ri-gift-line text-sm"></i>
                  FREE LIMITED-TIME BONUS
                </span>
              </div>

              {/* Headline */}
              <h2 className="text-center text-2xl font-black text-white leading-tight mb-2">
                Wait — Don't Leave Empty-Handed!
              </h2>
              <p className="text-center text-sm mb-3" style={{ color: '#aaa' }}>
                Grab your <strong style={{ color: '#F5D76E' }}>FREE 7-Day Fat-Loss Meal Plan</strong> — the exact blueprint Benny uses with his elite clients. Yours instantly, no strings attached.
              </p>

              {/* Social Proof Numbers */}
              <div
                className="flex items-center justify-center gap-4 rounded-xl py-3 px-4 mb-4"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.2)' }}
              >
                <div className="flex -space-x-2">
                  {[
                    'https://public.readdy.ai/ai/img_res/981b3081a02ddedc39edd0cccf2ea359.jpg',
                    'https://readdy.ai/api/search-image?query=professional%20african%20american%20man%20headshot%20portrait%20athletic%20smile%20clean%20simple%20background%20fitness&width=32&height=32&seq=sp2&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=professional%20black%20man%20headshot%20portrait%20happy%20smile%20clean%20simple%20background%20fitness&width=32&height=32&seq=sp3&orientation=squarish',
                  ].map((src, i) => (
                    <div
                      key={i}
                      className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0"
                      style={{ border: '2px solid #0a0a0a' }}
                    >
                      <img src={src} alt="member" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                <div className="text-left">
                  <p className="text-xs font-black" style={{ color: '#F5D76E' }}>
                    2,000+ people already downloaded this
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-xs" style={{ color: '#F5D76E' }}></i>
                    ))}
                    <span className="text-xs ml-1" style={{ color: '#888' }}>4.9/5 rating</span>
                  </div>
                </div>
              </div>

              {/* Countdown Timer */}
              <div
                className="flex flex-col items-center justify-center rounded-xl py-3 px-4 mb-5 transition-all duration-500"
                style={{
                  background: isUrgent ? 'rgba(255,68,68,0.12)' : 'rgba(184,134,11,0.1)',
                  border: `1px solid ${isUrgent ? 'rgba(255,68,68,0.5)' : 'rgba(184,134,11,0.3)'}`,
                }}
              >
                <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: isUrgent ? '#ff8888' : '#F5D76E' }}>
                  <i className="ri-timer-line mr-1"></i>
                  This offer expires in
                </p>
                <span
                  className="text-4xl font-black tabular-nums transition-all duration-300"
                  style={{
                    color: isUrgent ? '#ff4444' : '#F5D76E',
                    textShadow: isUrgent ? '0 0 20px rgba(255,68,68,0.6)' : '0 0 20px rgba(245,215,110,0.4)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatTime(timeLeft)}
                </span>
              </div>

              {/* What's inside */}
              <div
                className="rounded-xl p-4 mb-6 grid grid-cols-2 gap-2"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.2)' }}
              >
                {[
                  { icon: 'ri-fire-line', text: '7 days of fat-burning meals' },
                  { icon: 'ri-shopping-basket-line', text: 'Full grocery shopping list' },
                  { icon: 'ri-time-line', text: 'Prep in under 30 min/day' },
                  { icon: 'ri-trophy-line', text: 'Used by 500+ clients' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-sm`} style={{ color: '#F5D76E' }}></i>
                    </div>
                    <span className="text-xs" style={{ color: '#ccc' }}>{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
              >
                <input
                  type="text"
                  name="name"
                  placeholder="Your First Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(184,134,11,0.35)',
                    color: '#fff',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#F5D76E')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(184,134,11,0.35)')}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Best Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(184,134,11,0.35)',
                    color: '#fff',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#F5D76E')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(184,134,11,0.35)')}
                />

                {error && (
                  <p className="text-xs text-center" style={{ color: '#ff6b6b' }}>{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-lg font-black text-sm tracking-wide uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{
                    background: submitting ? '#888' : 'linear-gradient(90deg, #B8860B, #F5D76E)',
                    color: '#000',
                  }}
                >
                  {submitting ? 'Sending...' : '🎁 Send Me the Free Meal Plan'}
                </button>
              </form>

              <p className="text-center text-xs mt-3" style={{ color: '#555' }}>
                🔒 No spam. Unsubscribe anytime. We respect your privacy.
              </p>

              {/* Dismiss link */}
              <div className="text-center mt-3">
                <button
                  onClick={handlePermanentDismiss}
                  className="text-xs cursor-pointer underline transition-colors duration-200"
                  style={{ color: '#555' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#888')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#555')}
                >
                  No thanks, I don't want free results
                </button>
              </div>
            </>
          ) : (
            /* Success state */
            <div className="flex flex-col items-center py-6 text-center">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mb-4"
                style={{ background: 'rgba(184,134,11,0.15)', border: '2px solid #B8860B' }}
              >
                <i className="ri-check-line text-3xl" style={{ color: '#F5D76E' }}></i>
              </div>
              <h3 className="text-xl font-black text-white mb-2">You're In! 🎉</h3>
              <p className="text-sm mb-6" style={{ color: '#aaa' }}>
                Your <strong style={{ color: '#F5D76E' }}>FREE 7-Day Meal Plan</strong> is ready and waiting for you!
              </p>
              <a
                href="/meal-plan/view"
                onClick={handlePermanentDismiss}
                className="w-full py-3.5 rounded-xl font-black text-sm tracking-wide uppercase transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 hover:scale-105 shadow-xl text-center leading-snug"
                style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000' }}
              >
                <i className="ri-file-pdf-2-line text-lg flex-shrink-0"></i>
                <span>Your Meal Plan is Ready —<br />View &amp; Download PDF</span>
              </a>
              <button
                onClick={handlePermanentDismiss}
                className="mt-4 text-xs cursor-pointer"
                style={{ color: '#555' }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
